import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Rocket, TrendingDown, TrendingUp, AlertTriangle, RefreshCw, Zap, BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = "";
const ITEMS_PER_PAGE = 10;

// Skeleton loader component
const SkeletonCard = () => (
  <Card className="bg-gray-800 border-gray-700 animate-pulse">
    <CardContent className="p-4">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 bg-gray-700 rounded" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-700 rounded w-1/3" />
          <div className="h-3 bg-gray-700 rounded w-2/3" />
        </div>
        <div className="w-20 h-12 bg-gray-700 rounded" />
      </div>
    </CardContent>
  </Card>
);

export default function ExplosiveMovers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('rockets');
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/explosive-movers`);
      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error('Error fetching explosive movers:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  // Reset to page 1 when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const formatPremium = (premium) => {
    if (premium >= 1000000) return `$${(premium / 1000000).toFixed(1)}M`;
    if (premium >= 1000) return `$${(premium / 1000).toFixed(0)}K`;
    return `$${premium?.toFixed(0) || 0}`;
  };

  const getSentimentColor = (sentiment) => {
    if (sentiment?.includes('BULLISH')) return 'text-green-400';
    if (sentiment?.includes('BEARISH')) return 'text-red-400';
    return 'text-yellow-400';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-green-400';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-gray-500';
  };

  // Get current list based on tab
  const getCurrentList = () => {
    if (activeTab === 'rockets') return data?.rockets || [];
    if (activeTab === 'crashes') return data?.crashes || [];
    return data?.whale_alerts || [];
  };

  // Pagination logic
  const currentList = getCurrentList();
  const totalPages = Math.ceil(currentList.length / ITEMS_PER_PAGE);
  const paginatedItems = currentList.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Loading skeleton
  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-3 sm:p-6">
        <div className="flex items-center gap-2 mb-6">
          <Rocket className="w-8 h-8 text-purple-400 animate-bounce" />
          <h1 className="text-xl font-bold">Scanning Market...</h1>
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-3 sm:p-6" data-testid="explosive-movers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold flex items-center gap-2">
            <Rocket className="text-purple-400 w-6 h-6 sm:w-8 sm:h-8" />
            Explosive Movers
          </h1>
          <p className="text-gray-400 text-xs sm:text-sm mt-1">
            🐋 Find stocks before they 100x
          </p>
        </div>
        <Button 
          onClick={fetchData} 
          variant="outline" 
          size="sm"
          className="border-purple-500 text-purple-400 hover:bg-purple-500/20 w-full sm:w-auto"
          disabled={loading}
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Market Sentiment - Compact Mobile */}
      {data?.market_sentiment && (
        <Card className="bg-gray-800 border-gray-700 mb-4">
          <CardContent className="p-3">
            <div className="grid grid-cols-4 gap-2 text-center">
              <div>
                <p className="text-[10px] sm:text-xs text-gray-400">Sentiment</p>
                <p className={`text-sm sm:text-lg font-bold ${getSentimentColor(data.market_sentiment.sentiment)}`}>
                  {data.market_sentiment.sentiment?.split(' ')[0] || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-[10px] sm:text-xs text-gray-400">C/P</p>
                <p className="text-sm sm:text-lg font-semibold text-white">
                  {data.market_sentiment.call_put_ratio?.toFixed(2) || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-[10px] sm:text-xs text-gray-400">Calls</p>
                <p className="text-sm sm:text-lg text-green-400">
                  {formatPremium(data.market_sentiment.net_call_premium || 0)}
                </p>
              </div>
              <div>
                <p className="text-[10px] sm:text-xs text-gray-400">Scanned</p>
                <p className="text-sm sm:text-lg font-bold text-purple-400">
                  {(data.stats?.total_stocks_scanned || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Row - Mobile Compact */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        <Card className="bg-gradient-to-br from-green-900/50 to-gray-800 border-green-700/50">
          <CardContent className="p-2 sm:p-4 text-center">
            <p className="text-[10px] sm:text-xs text-gray-400">🚀 Rockets</p>
            <p className="text-lg sm:text-2xl font-bold text-green-400">{data?.rockets?.length || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-900/50 to-gray-800 border-red-700/50">
          <CardContent className="p-2 sm:p-4 text-center">
            <p className="text-[10px] sm:text-xs text-gray-400">💥 Crashes</p>
            <p className="text-lg sm:text-2xl font-bold text-red-400">{data?.crashes?.length || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-900/50 to-gray-800 border-purple-700/50">
          <CardContent className="p-2 sm:p-4 text-center">
            <p className="text-[10px] sm:text-xs text-gray-400">🐋 Whales</p>
            <p className="text-lg sm:text-2xl font-bold text-purple-400">{data?.stats?.whale_flow_alerts || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-900/50 to-gray-800 border-blue-700/50">
          <CardContent className="p-2 sm:p-4 text-center">
            <p className="text-[10px] sm:text-xs text-gray-400">📊 Analyzed</p>
            <p className="text-lg sm:text-2xl font-bold text-blue-400">{data?.stats?.deep_technicals || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Mobile-Optimized Tabs */}
      <div className="flex gap-1 sm:gap-2 mb-4 overflow-x-auto pb-2">
        <Button
          variant={activeTab === 'rockets' ? 'default' : 'outline'}
          onClick={() => setActiveTab('rockets')}
          className={`flex-1 sm:flex-none text-xs sm:text-sm ${activeTab === 'rockets' ? 'bg-green-600 hover:bg-green-700' : 'border-gray-600'}`}
          data-testid="tab-rockets"
        >
          <Rocket className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">🚀 </span>Rockets ({data?.rockets?.length || 0})
        </Button>
        <Button
          variant={activeTab === 'crashes' ? 'default' : 'outline'}
          onClick={() => setActiveTab('crashes')}
          className={`flex-1 sm:flex-none text-xs sm:text-sm ${activeTab === 'crashes' ? 'bg-red-600 hover:bg-red-700' : 'border-gray-600'}`}
          data-testid="tab-crashes"
        >
          <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">💥 </span>Crashes ({data?.crashes?.length || 0})
        </Button>
        <Button
          variant={activeTab === 'all' ? 'default' : 'outline'}
          onClick={() => setActiveTab('all')}
          className={`flex-1 sm:flex-none text-xs sm:text-sm ${activeTab === 'all' ? 'bg-purple-600 hover:bg-purple-700' : 'border-gray-600'}`}
          data-testid="tab-all-whales"
        >
          <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">🐋 </span>All ({data?.whale_alerts?.length || 0})
        </Button>
      </div>

      {/* Content - Paginated */}
      <div className="space-y-3">
        {paginatedItems.map((item, index) => (
          <Card 
            key={`${item.ticker}-${index}`} 
            className={`bg-gray-800 transition-colors ${
              activeTab === 'rockets' ? 'border-green-700/50 hover:border-green-500' :
              activeTab === 'crashes' ? 'border-red-700/50 hover:border-red-500' :
              'border-gray-700 hover:border-purple-500'
            }`}
            data-testid={`${activeTab}-${item.ticker}`}
          >
            <CardContent className="p-3 sm:p-4">
              {/* Mobile Layout */}
              <div className="flex items-start gap-3">
                {/* Ticker & Score */}
                <div className="text-center min-w-[60px] sm:min-w-[80px]">
                  <p className="text-lg sm:text-2xl font-bold text-white">{item.ticker}</p>
                  <Badge className={`${getScoreColor(item.score || item.explosive_score)} text-white text-[10px] sm:text-xs`}>
                    {item.score || item.explosive_score || 'N/A'}
                  </Badge>
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {activeTab === 'rockets' ? (
                      <TrendingUp className="w-4 h-4 text-green-400 flex-shrink-0" />
                    ) : activeTab === 'crashes' ? (
                      <TrendingDown className="w-4 h-4 text-red-400 flex-shrink-0" />
                    ) : item.direction === 'BULLISH' ? (
                      <TrendingUp className="w-4 h-4 text-green-400 flex-shrink-0" />
                    ) : item.direction === 'BEARISH' ? (
                      <TrendingDown className="w-4 h-4 text-red-400 flex-shrink-0" />
                    ) : (
                      <BarChart3 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                    <span className={`text-xs sm:text-sm font-medium ${
                      activeTab === 'rockets' || item.direction === 'BULLISH' ? 'text-green-400' :
                      activeTab === 'crashes' || item.direction === 'BEARISH' ? 'text-red-400' :
                      'text-gray-300'
                    }`}>
                      {item.direction}
                    </span>
                    {item.has_sweep && (
                      <Badge className="bg-yellow-500 text-black text-[10px] px-1 py-0">
                        <Zap className="w-2 h-2 mr-0.5" />
                        SWEEP
                      </Badge>
                    )}
                  </div>
                  
                  {/* Reasons - Truncated for mobile */}
                  <div className="flex flex-wrap gap-1">
                    {(item.factors || item.reasons)?.slice(0, 2).map((reason, i) => (
                      <span key={i} className="text-[10px] sm:text-xs text-gray-300 bg-gray-700 px-1.5 py-0.5 rounded truncate max-w-[120px] sm:max-w-none">
                        {reason}
                      </span>
                    ))}
                    {(item.factors || item.reasons)?.length > 2 && (
                      <span className="text-[10px] text-gray-500">+{(item.factors || item.reasons).length - 2}</span>
                    )}
                  </div>
                </div>

                {/* Premium */}
                <div className="text-right flex-shrink-0">
                  <p className={`text-sm sm:text-lg font-bold ${
                    activeTab === 'rockets' ? 'text-green-400' :
                    activeTab === 'crashes' ? 'text-red-400' :
                    'text-purple-400'
                  }`}>
                    {formatPremium(item.total_premium)}
                  </p>
                  <p className="text-[10px] sm:text-xs text-gray-500">
                    {item.trade_count ? `${item.trade_count} trades` : ''}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {paginatedItems.length === 0 && !loading && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6 sm:p-8 text-center">
            <AlertTriangle className="w-10 h-10 sm:w-12 sm:h-12 text-yellow-500 mx-auto mb-3" />
            <p className="text-lg sm:text-xl text-gray-300">No {activeTab} detected</p>
            <p className="text-gray-500 text-sm mt-1">Market is quiet or data is loading</p>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="border-gray-600"
            data-testid="prev-page"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-gray-400">
            {currentPage} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="border-gray-600"
            data-testid="next-page"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 text-center text-gray-500 text-xs sm:text-sm">
        <p>Last updated: {data?.generated_at ? new Date(data.generated_at).toLocaleString() : 'N/A'}</p>
        <p className="mt-1">🐋 Powered by Unusual Whales Premium</p>
      </div>
    </div>
  );
}
